import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { toast } from 'sonner';
import { RefreshCw, Search, CheckCircle2, XCircle, Copy, Send } from 'lucide-react';

const STATUS_META = {
  sent: { label: 'Enviado', cls: 'bg-emerald-500/15 text-emerald-600 border-emerald-500' },
  failed: { label: 'Falló', cls: 'bg-red-500/15 text-red-600 border-red-500' },
  deduped: { label: 'Duplicado', cls: 'bg-amber-500/15 text-amber-600 border-amber-500' },
};

function fmt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
}

export default function EmailLogPanel({ api }) {
  const [logs, setLogs] = useState([]);
  const [counts, setCounts] = useState({ sent: 0, failed: 0, deduped: 0 });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [resendTarget, setResendTarget] = useState(null);
  const [resending, setResending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (status) params.set('status', status);
      if (q) params.set('q', q);
      const res = await api().get(`/config/email-log?${params.toString()}`);
      setLogs(res.data.logs || []);
      setCounts(res.data.counts_7d || { sent: 0, failed: 0, deduped: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cargar el historial');
    } finally {
      setLoading(false);
    }
  }, [api, status, q]);

  useEffect(() => { load(); }, [load]);

  const doResend = async () => {
    if (!resendTarget) return;
    setResending(true);
    try {
      const res = await api().post(`/config/email-log/${resendTarget.id}/resend`);
      if (res.data.ok) toast.success(`Reenviado a ${res.data.to} (${res.data.provider})`);
      else toast.error(`No se pudo reenviar: ${res.data.error || 'error del proveedor'}`);
      setResendTarget(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo reenviar');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="email-log-panel">
      <div className="grid grid-cols-3 gap-2">
        {['sent', 'failed', 'deduped'].map((k) => (
          <Card key={k} className="cursor-pointer" onClick={() => setStatus(status === k ? '' : k)} data-testid={`email-log-count-${k}`}>
            <CardContent className="py-3 text-center">
              <div className={`text-2xl font-bold ${k === 'sent' ? 'text-emerald-600' : k === 'failed' ? 'text-red-600' : 'text-amber-600'}`}>{counts[k]}</div>
              <div className="text-xs text-muted-foreground">{STATUS_META[k].label} (7 días){status === k ? ' •' : ''}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
            placeholder="Buscar por correo o asunto…"
            className="h-9 pl-8"
            data-testid="email-log-search"
          />
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="email-log-reload">
          <RefreshCw className="w-4 h-4 mr-1" /> {loading ? '…' : 'Actualizar'}
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="email-log-table">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="p-2">Fecha</th>
                  <th className="p-2">Estado</th>
                  <th className="p-2">Para</th>
                  <th className="p-2">Asunto</th>
                  <th className="p-2">Proveedor</th>
                  <th className="p-2 text-right">Acción</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center text-muted-foreground text-xs" data-testid="email-log-empty">
                    Sin envíos registrados todavía. Aquí aparecerá cada correo automático (a quién, cuándo y con qué proveedor).
                  </td></tr>
                )}
                {logs.map((l) => {
                  const m = STATUS_META[l.status] || { label: l.status, cls: '' };
                  const ccCount = (l.cc || []).length;
                  return (
                    <tr key={l.id} className="border-b hover:bg-muted/40" data-testid={`email-log-row-${l.id}`}>
                      <td className="p-2 whitespace-nowrap text-xs">{fmt(l.created_at)}</td>
                      <td className="p-2">
                        <Badge variant="outline" className={`${m.cls} flex items-center gap-1 w-fit`}>
                          {l.status === 'sent' ? <CheckCircle2 className="w-3 h-3" /> : l.status === 'failed' ? <XCircle className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          {m.label}
                        </Badge>
                      </td>
                      <td className="p-2 text-xs">
                        {l.to}
                        {ccCount > 0 && <span className="text-muted-foreground"> +{ccCount} cc</span>}
                      </td>
                      <td className="p-2 text-xs max-w-[280px] truncate" title={l.subject}>{l.subject}</td>
                      <td className="p-2 text-xs capitalize">{l.provider || '—'}</td>
                      <td className="p-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2"
                          onClick={() => setResendTarget(l)}
                          data-testid={`email-log-resend-${l.id}`}
                        >
                          <Send className="w-3.5 h-3.5 mr-1" /> Reenviar
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={!!resendTarget} onOpenChange={(o) => { if (!o) setResendTarget(null); }}>
        <AlertDialogContent data-testid="email-log-resend-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reenviar correo</AlertDialogTitle>
            <AlertDialogDescription>
              Se reenviará el mismo correo <b>"{resendTarget?.subject}"</b> a{' '}
              <b>{resendTarget?.to}</b>
              {resendTarget?.cc?.length > 0 && <> (+{resendTarget.cc.length} en copia)</>}.
              Se envía de inmediato, sin filtro de duplicados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="email-log-resend-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doResend} disabled={resending} data-testid="email-log-resend-confirm">
              {resending ? 'Reenviando…' : 'Reenviar ahora'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
